/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, useRef, useState, type CSSProperties } from 'react';

import { cssMap, jsx } from '@compiled/react';

import IconButton from '@atlaskit/button/icon/button';
import GenerativeIndicatorIcon from '@atlaskit/icon-lab/core/generative-indicator';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { TooltipProps } from '@atlaskit/tooltip/types';

import { SuggestionAvatar, type SuggestionAvatarProfile } from './SuggestionAvatar';

type IconButtonTooltipProps = Partial<Omit<TooltipProps, 'children'>>;

// Number of visible avatars, set inline per-render (see the root `span`'s `style`). Drives
// `indicatorBase`'s width below, so this real flex item grows and pushes sibling right-surface
// controls instead of overlapping them.
const avatarCountVar = '--suggestion-avatar-count';

const MAX_VISIBLE_AVATARS = 3;

// Shared by `indicatorActive` and `avatarStackPillRevealed` below — the container and the pill
// must grow by the exact same formula, or the pill overflows its flex slot.
const stackWidthCalc = `calc(${token('space.300')} + ${token('space.150')} * (var(${avatarCountVar}, 1) - 1))`;

const avatarStackPillRevealTransition = `opacity ${token('motion.duration.xshort')} ${token('motion.easing.out.practical')}, width ${token('motion.duration.medium')} ${token('motion.easing.out.practical')}`;

const styles = cssMap({
	indicatorBase: {
		position: 'relative',
		display: 'block',
		height: token('space.300'),
		width: token('space.300'),
		transition: `width ${token('motion.duration.medium')} ${token('motion.easing.out.practical')}`,
	},
	indicatorActive: {
		width: stackWidthCalc,
	},
	iconLayer: {
		position: 'absolute',
		inset: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		opacity: 1,
		transition: `opacity ${token('motion.duration.xshort')} ${token('motion.easing.out.practical')}`,
	},
	iconLayerHidden: {
		opacity: 0,
	},
	// Owns the reveal animation only (position, size, opacity) — kept off the `<button>` itself so
	// its colour transitions below never share (and clobber) this element's `transition`.
	avatarStackPillWrapper: {
		position: 'absolute',
		top: 0,
		left: 0,
		boxSizing: 'border-box',
		height: token('space.300'),
		width: token('space.300'),
		opacity: 0,
		transition: avatarStackPillRevealTransition,
	},
	avatarStackPillWrapperRevealed: {
		opacity: 1,
		width: stackWidthCalc,
	},
	avatarStackPill: {
		display: 'flex',
		alignItems: 'center',
		height: '100%',
		width: '100%',
		border: 'none',
		borderRadius: token('radius.full'),
		backgroundColor: token('elevation.rovo.surface.overlay'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.075'),
		cursor: 'pointer',
		font: 'inherit',
	},
	avatarStackPillInteractive: {
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('elevation.rovo.surface.overlay.hovered'),
			boxShadow: token('elevation.shadow.overflow'),
		},
		'&:active': {
			backgroundColor: token('elevation.rovo.surface.overlay.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
	avatarStackRow: {
		display: 'flex',
		alignItems: 'center',
		isolation: 'isolate',
	},
	avatar: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		height: token('space.150'),
		width: token('space.150'),
	},
	avatarSlide: {
		transition: `transform ${token('motion.duration.short')} ${token('motion.easing.out.practical')} ${token('motion.duration.xxshort')}`,
	},
});

const AnimatedIcon = () => (
	<span css={styles.iconLayer}>
		<GenerativeIndicatorIcon label="" size="small" color={token('color.icon.subtle')} />
	</span>
);

export type SuggestionAvatarIndicatorProps = {
	isActive: boolean;
	label: string;
	onBlur?: () => void;
	onClick?: () => void;
	onFocus?: () => void;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	/** Unique agent profiles behind the current suggestion(s) — human authors are not shown. */
	profiles: SuggestionAvatarProfile[];
	tooltip: IconButtonTooltipProps;
};

export function SuggestionAvatarIndicator({
	isActive,
	label,
	onBlur,
	onClick,
	onFocus,
	onMouseEnter,
	onMouseLeave,
	profiles,
	tooltip,
}: SuggestionAvatarIndicatorProps): JSX.Element {
	// Hovering/focusing this icon directly also reveals the stack, on top of `isActive`.
	const [isHovered, setIsHovered] = useState(false);
	const showAvatars = (isActive || isHovered) && profiles.length > 0;
	const visibleProfiles = profiles.slice(0, MAX_VISIBLE_AVATARS);
	const containerRef = useRef<HTMLSpanElement | null>(null);
	// `@atlaskit/tooltip` only flips vertically for viewport overflow, not horizontally — so a
	// bubble near the right edge still gets cropped. We measure the trigger ourselves and switch
	// our requested alignment from 'start' to 'end' to work around that.
	const [horizontalAlign, setHorizontalAlign] = useState<'start' | 'end'>('start');

	const measureHorizontalAlign = useCallback(() => {
		const node = containerRef.current;
		if (!node) {
			return;
		}
		// Matches `SuggestionTooltip`'s `maxWidth`.
		const tooltipMaxWidth = 240;
		const { left } = node.getBoundingClientRect();
		setHorizontalAlign(left + tooltipMaxWidth > window.innerWidth ? 'end' : 'start');
	}, []);

	const handleMouseEnter = useCallback(() => {
		measureHorizontalAlign();
		setIsHovered(true);
		onMouseEnter?.();
	}, [measureHorizontalAlign, onMouseEnter]);

	const handleMouseLeave = useCallback(() => {
		setIsHovered(false);
		onMouseLeave?.();
	}, [onMouseLeave]);

	const handleFocus = useCallback(() => {
		measureHorizontalAlign();
		setIsHovered(true);
		onFocus?.();
	}, [measureHorizontalAlign, onFocus]);

	const handleBlur = useCallback(() => {
		setIsHovered(false);
		onBlur?.();
	}, [onBlur]);

	const resolvedTooltip = useMemo(() => {
		const verticalSide =
			typeof tooltip.position === 'string' ? tooltip.position.split('-')[0] : 'top';
		return {
			...tooltip,
			position: `${verticalSide}-${horizontalAlign}` as IconButtonTooltipProps['position'],
		};
	}, [tooltip, horizontalAlign]);

	return (
		<span
			ref={containerRef}
			css={[styles.indicatorBase, showAvatars && styles.indicatorActive]}
			style={{ [avatarCountVar]: visibleProfiles.length || 1 } as CSSProperties}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={handleFocus}
			onBlur={handleBlur}
		>
			<span
				css={[styles.iconLayer, showAvatars && styles.iconLayerHidden]}
				style={{ pointerEvents: showAvatars ? 'none' : 'auto' }}
			>
				<IconButton
					isTooltipDisabled={false}
					tooltip={resolvedTooltip}
					shape="circle"
					type="button"
					label={label}
					onClick={onClick}
					appearance="subtle"
					spacing="compact"
					icon={AnimatedIcon}
				/>
			</span>
			{profiles.length > 0 && (
				<span
					css={[
						styles.avatarStackPillWrapper,
						showAvatars && styles.avatarStackPillWrapperRevealed,
					]}
					style={{ pointerEvents: showAvatars ? 'auto' : 'none' }}
				>
					<Tooltip
						content={resolvedTooltip.content ?? label}
						position={resolvedTooltip.position}
						component={resolvedTooltip.component}
						delay={resolvedTooltip.delay}
						ignoreTooltipPointerEvents={resolvedTooltip.ignoreTooltipPointerEvents}
					>
						<button
							type="button"
							css={[styles.avatarStackPill, styles.avatarStackPillInteractive]}
							onClick={onClick}
							aria-label={label}
						>
							<span css={styles.avatarStackRow}>
								{visibleProfiles.map((avatarProfile, index) => (
									<span
										key={avatarProfile.profile?.accountId ?? index}
										css={[styles.avatar, styles.avatarSlide]}
										style={{
											// Earlier avatars stack in front of later ones.
											zIndex: visibleProfiles.length - index,
											// Hidden: each avatar slides left by index * 12px (its slot width),
											// stacking on avatar 0. Revealed: no offset, natural flex position.
											transform: `translateX(${showAvatars ? 0 : -index * 12}px)`,
										}}
									>
										<SuggestionAvatar profile={avatarProfile.profile} />
									</span>
								))}
							</span>
						</button>
					</Tooltip>
				</span>
			)}
		</span>
	);
}
